import React, { createContext, useState, useEffect, useContext } from 'react';
import { ApiService, ApiEndpoints } from '../services/ApiService';

/**
 * User interface
 */
interface User {
  id: string | number;
  name: string;
  email: string;
  role: string;
  companyId?: number;
  departmentId?: number;
  teamId?: number;
}

/**
 * Authentication context interface
 */
interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
}

/**
 * Create authentication context
 */
const SimpleAuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Authentication provider component
 */
export const SimpleAuthProvider: React.FC<{children: React.ReactNode}> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Check for existing token on mount
  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      fetchUserProfile();
    } else {
      setLoading(false);
    }
  }, []);
  
  /**
   * Fetch user profile from API
   */
  const fetchUserProfile = async () => {
    try {
      setLoading(true);
      const userData = await ApiService.get<User>(ApiEndpoints.AUTH.PROFILE);
      setUser(userData);
    } catch (err) {
      console.error('Error fetching user profile:', err);
      setError('Session expired. Please login again.');
      logout();
    } finally {
      setLoading(false);
    }
  };
  
  /**
   * Login user with email and password
   */
  const login = async (email: string, password: string) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await ApiService.post<{token: string; user: User}>(ApiEndpoints.AUTH.LOGIN, { email, password });
      
      if (response.token) {
        localStorage.setItem('auth_token', response.token);
        setUser(response.user);
      } else {
        throw new Error('No token received');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError('Login failed. Please check your credentials.');
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  /**
   * Logout user and clear authentication
   */
  const logout = () => {
    localStorage.removeItem('auth_token');
    setUser(null);
  };
  
  // Context value
  const value = {
    user,
    loading,
    error,
    login,
    logout,
    isAuthenticated: !!user
  };
  
  return (
    <SimpleAuthContext.Provider value={value}>
      {children}
    </SimpleAuthContext.Provider>
  );
};

/**
 * Custom hook to use authentication context
 */
export const useAuth = () => {
  const context = useContext(SimpleAuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within a SimpleAuthProvider');
  }
  return context;
};
